import { Component, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import html2pdf from 'html2pdf.js';
import { DialogService } from 'primeng/dynamicdialog';

import { CustomerService } from 'src/app/services/customer.service';
import { CustomReportExportComponent } from 'src/app/components/extract-custom-report/custom-report-export.component';
import { MessageUtil } from 'src/app/utils/message.util';

@Component({
  selector: 'app-dependents',
  templateUrl: './dependents.component.html',
  styleUrls: ['./dependents.component.scss']
})
export class DependentsComponent implements OnInit {
  customers: any[] = [];
  filteredCustomers: any[] = [];
  filters: any = {};
  exportData: any[] = [];
  titleNumberMap: Map<string, any> = new Map();

  public statusOptions: Array<{ label: string, value: any }> = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
    { label: 'Todos', value: null }
  ];

  public dependentsOptions: Array<{ label: string, value: any }> = [
    { label: 'Com Dependentes', value: 'with' },
    { label: 'Sem Dependentes', value: 'without' },
    { label: 'Todos', value: null }
  ];

  constructor(
    private spinner: NgxSpinnerService,
    private customerService: CustomerService,
    private dialog: DialogService,
    private messageUtil: MessageUtil
  ) {}

  ngOnInit(): void {
    this.initializeFilters();
  }

  initializeFilters(): void {
    this.filters = {
      titleNumber: null,
      codRegistration: null,
      name: null,
      status: null,
      dependentsFilter: 'with'
    };
  }

  loadCustomers(): void {
    this.spinner.show();
    this.customerService.getCustomerDependentsByMarina().subscribe({
      next: (response) => {
        this.customers = response;
        this.buildTitleNumberMap();
        this.applyFilters();
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Erro ao carregar dependentes:', error);
        this.spinner.hide();
      }
    });
  }

  buildTitleNumberMap(): void {
    this.titleNumberMap.clear();
    this.customers.forEach(customer => {
      if (customer.titleNumber) {
        const titleNumber = customer.titleNumber.toString().trim();
        this.titleNumberMap.set(titleNumber, customer);
      }
    });
  }

  buildPDF(): void {
    this.spinner.show();
    const element = document.getElementById('customersTable');
    const opt = {
      margin: 0,
      filename: 'dependentsReport.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, logging: true, useCORS: true },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'landscape',
        compress: true
      }
    };

    html2pdf()
      .from(element)
      .set(opt)
      .save()
      .then(() => {
        this.spinner.hide();
      })
      .catch(error => {
        console.error('Erro ao gerar PDF:', error);
        this.spinner.hide();
      });
  }
  
  async exportTable(): Promise<void> {
    await this.prepareDataForExport();
    const data = { table: this.exportData, type: 'DEPENDENTS' };
    this.dialog.open(CustomReportExportComponent, {
      width: '70%',
      height: '90vh',
      dismissableMask: false,
      data,
      header: this.messageUtil.translateKey('EXPORT-DEPENDENTS-TABLE')
    });
  }
  
  async prepareDataForExport(): Promise<void> {
    return new Promise((resolve) => {
      this.spinner.show();
      this.exportData = [];
      
      this.filteredCustomers.forEach(customer => {
        let dependentsString = '';
        
        if (customer.dependents && customer.dependents.length > 0) {
          const dependentStrings = customer.dependents.map(dependent => {
            return `${dependent.codRegistration} - ${dependent.active ? 'Ativo' : 'Inativo'} - ${dependent.name} - ${dependent.phone || '-'}`;
          });
          dependentsString = dependentStrings.join('\n'); // Usando quebra de linha em vez de ' / '
        } else {
          dependentsString = 'Nenhum dependente';
        }
        
        const customerData = {
          titleNumber: customer.titleNumber || '-',
          status: customer.active ? 'Ativo' : 'Inativo',
          name: `${customer.codRegistration || 'N/A'} - ${customer.name}`,
          phone: customer.phone || '-',
          dependents: dependentsString
        };
        
        this.exportData.push(customerData);
      });
      
      this.spinner.hide();
      resolve();
    });
  }

  applyFilters(): void {
    this.spinner.show();

    // Busca direta por Número do Título (lookup exato via mapa).
    // Mesmo aqui o filtro de Status precisa valer: se o titular não bate com
    // o status selecionado, não deve ser retornado.
    if (this.filters.titleNumber && this.filters.titleNumber.toString().trim() !== '') {
      const titleFilterValue = this.filters.titleNumber.toString().trim();
      const matchingCustomer = this.titleNumberMap.get(titleFilterValue);
      if (matchingCustomer && this.matchesStatus(matchingCustomer.active)) {
        this.filteredCustomers = [this.buildDisplayCustomer(matchingCustomer)];
      } else {
        this.filteredCustomers = [];
      }
      this.spinner.hide();
      return;
    }

    this.filteredCustomers = this.customers
      .filter(customer => this.customerPassesFilters(customer))
      .map(customer => this.buildDisplayCustomer(customer));

    this.spinner.hide();
  }

  // Regra de inclusão do titular (linha do relatório).
  // O filtro de Status gatilha o titular: um cadastro que não bate com o
  // status selecionado nunca é listado como linha. Nome/Matrícula são busca
  // (casam no titular OU em algum dependente visível). "Com/Sem dependentes"
  // considera apenas os dependentes visíveis (já podados por status).
  private customerPassesFilters(customer: any): boolean {
    if (!this.matchesStatus(customer.active)) {
      return false;
    }

    const hasNameFilter = this.filters.name && this.filters.name.trim() !== '';
    const hasCodFilter = this.filters.codRegistration && this.filters.codRegistration.toString().trim() !== '';
    const visibleDependents = this.getVisibleDependents(customer);

    if (hasNameFilter || hasCodFilter) {
      const titularMatches = this.matchesName(customer.name) && this.matchesCod(customer.codRegistration);
      const dependentMatches = visibleDependents.some(dependent =>
        this.matchesName(dependent.name) && this.matchesCod(dependent.codRegistration)
      );
      if (!titularMatches && !dependentMatches) {
        return false;
      }
    }

    if (this.filters.dependentsFilter === 'with') {
      return visibleDependents.length > 0;
    }
    if (this.filters.dependentsFilter === 'without') {
      return visibleDependents.length === 0;
    }

    return true;
  }

  // Dependentes exibidos para um titular: apenas os que batem com o filtro de
  // Status. É isto que garante que, com Status = Ativo, nenhum dependente
  // inativo apareça na coluna "Dependentes" (nem no export/PDF).
  private getVisibleDependents(customer: any): any[] {
    const dependents = customer.dependents || [];
    return dependents.filter(dependent => this.matchesStatus(dependent.active));
  }

  // Cópia do titular com a lista de dependentes já podada por status, sem
  // mutar this.customers (fonte original consumida pelo mapa de títulos).
  private buildDisplayCustomer(customer: any): any {
    return { ...customer, dependents: this.getVisibleDependents(customer) };
  }

  private matchesStatus(active: boolean): boolean {
    if (this.filters.status === null || this.filters.status === undefined) {
      return true;
    }
    return active === this.filters.status;
  }

  private matchesName(name: string): boolean {
    if (!this.filters.name || this.filters.name.trim() === '') {
      return true;
    }
    return !!name && name.toLowerCase().includes(this.filters.name.toLowerCase());
  }

  private matchesCod(codRegistration: any): boolean {
    if (!this.filters.codRegistration || this.filters.codRegistration.toString().trim() === '') {
      return true;
    }
    return codRegistration != null &&
      codRegistration.toString().trim() === this.filters.codRegistration.toString().trim();
  }

  clearFilters(): void {
    this.filters = {
      titleNumber: null,
      codRegistration: null,
      name: null,
      status: null,
      dependentsFilter: 'with'
    };
    this.findCustomers();
  }

  findCustomers(): void {
    this.applyFilters();
  }

  getPhoneFormatted(phone: string): string {
    return phone || '-';
  }

  getStatusLabel(status: boolean): string {
    return status ? 'Ativo' : 'Inativo';
  }

  getFormattedHolderName(customer: any): string {
    const codRegistration = customer.codRegistration || 'Sem Matrícula';
    return `${codRegistration} - ${customer.name}`;
  }

  getFormattedDependentCodRegistration(codRegistration: string): string {
    return codRegistration || 'Sem Matrícula';
  }

  getTitleNumber(customer: any): string {
    return customer.titleNumber || '-';
  }

  onlyNumbers(event: any): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      return false;
    }
    return true;
  }

  onCodRegistrationInput(event: any): void {
    let value = event.target.value;
    
    value = value.replace(/\D/g, '');
    
    if (value.length > 6) {
      value = value.substring(0, 6) + '.' + value.substring(6, 8);
    }
    
    this.filters.codRegistration = value;
    
    event.target.value = value;
  }
  

}
